<template>
  <div class="min-h-screen bg-[#FAF8F8] px-4 sm:px-6 lg:px-8 py-8">
    <div class="max-w-7xl mx-auto space-y-6">

      <!-- En-tête Kanban -->
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div class="flex items-center gap-3">
            <h1 class="text-2xl sm:text-3xl font-extrabold text-[#0B1B3D] tracking-tight">
              Tableau Kanban
            </h1>
            <span
              class="bg-gradient-to-r from-[#7C3AED] to-[#F97316] text-white text-[10px] uppercase tracking-wider font-extrabold px-2.5 py-0.5 rounded-full shadow-2xs">
              Interactif
            </span>
          </div>
          <p class="text-gray-500 text-sm mt-1">
            Visualisez et déplacez vos projets à travers les étapes de production.
          </p>
        </div>

        <!-- Filtre rapide par type -->
        <div class="flex items-center gap-3">
          <label class="text-xs font-semibold text-gray-500">Filtrer par type :</label>
          <select v-model="selectedType"
            class="text-xs font-semibold p-2.5 bg-white border border-gray-200 rounded-xl focus:border-[#7C3AED] outline-none shadow-2xs">
            <option value="Tous">Tous les types</option>
            <option v-for="type in availableTypes" :key="type" :value="type">{{ type }}</option>
          </select>
        </div>
      </div>

      <!-- État Chargement -->
      <div v-if="isLoading" class="py-20 text-center flex flex-col items-center gap-3">
        <svg class="animate-spin h-8 w-8 text-[#7C3AED]" xmlns="http://www.w3.org/2000/svg" fill="none"
          viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
        </svg>
        <span class="text-sm font-medium text-gray-500">Synchronisation du Kanban...</span>
      </div>

      <!-- Colonnes du Kanban -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-start">

        <div v-for="column in columns" :key="column.status"
          class="bg-[#F4F3F3] rounded-2xl p-4 border border-gray-200/70 flex flex-col min-h-[500px]">
          <!-- En-tête de la colonne -->
          <div class="flex items-center justify-between pb-3 mb-3 border-b border-gray-200">
            <div class="flex items-center gap-2">
              <span class="w-2.5 h-2.5 rounded-full" :class="column.dotColor"></span>
              <h2 class="text-sm font-bold text-[#0B1B3D]">{{ column.status }}</h2>
            </div>
            <span class="text-xs font-bold px-2 py-0.5 rounded-full bg-white text-gray-700 shadow-2xs">
              {{ getProjectsByStatus(column.status).length }}
            </span>
          </div>

          <!-- Liste des cartes de projets dans cette colonne -->
          <div class="space-y-3 flex-1 overflow-y-auto pr-0.5">
            <div v-for="project in getProjectsByStatus(column.status)" :key="project.id"
              class="bg-white rounded-xl p-4 border border-gray-100 shadow-xs hover:shadow-md hover:border-[#7C3AED]/30 transition-all duration-150 cursor-pointer flex flex-col justify-between gap-3 group"
              @click="$router.push(`/projects/${project.id}`)">
              <!-- En-tête de la carte -->
              <div>
                <div class="flex items-start justify-between gap-2">
                  <h3 class="text-sm font-bold text-[#0B1B3D] group-hover:text-[#7C3AED] transition leading-snug">
                    {{ project.title }}
                  </h3>
                  <span
                    class="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap bg-purple-50 text-[#7C3AED]">
                    {{ project.project_type || 'Standard' }}
                  </span>
                </div>
                <p v-if="project.description" class="text-xs text-gray-500 mt-1.5 line-clamp-2 leading-relaxed">
                  {{ project.description }}
                </p>
              </div>

              <!-- Lots & Lien détail -->
              <div class="flex items-center justify-between text-[11px] text-gray-400 pt-2 border-t border-gray-50">
                <span class="font-medium text-gray-500">
                  {{ project.lots_count ? `${project.lots_count} lot(s)` : 'Projet direct' }}
                </span>
                <span class="text-[#F97316] font-semibold group-hover:translate-x-0.5 transition-transform">
                  Détails →
                </span>
              </div>

              <!-- Action rapide : changer de statut -->
              <div class="pt-2 border-t border-gray-50 flex items-center justify-between gap-1" @click.stop>
                <span class="text-[10px] text-gray-400">Déplacer :</span>
                <select :value="project.status" @change="moveProject(project, $event.target.value)"
                  class="text-[11px] font-bold p-1 bg-gray-50 border border-gray-200 rounded-lg outline-none cursor-pointer">
                  <option v-for="c in columns" :key="c.status" :value="c.status">
                    {{ c.status }}
                  </option>
                </select>
              </div>
            </div>

            <!-- État vide de la colonne -->
            <div v-if="getProjectsByStatus(column.status).length === 0"
              class="h-32 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center text-xs text-gray-400 text-center p-4">
              Aucun projet
            </div>
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<script>
import apiClient from '@/services/apiClient'

export default {
  name: 'KanbanView',
  data() {
    return {
      projects: [],
      selectedType: 'Tous',
      isLoading: false,
      columns: [
        { status: 'Brouillon', dotColor: 'bg-gray-400' },
        { status: 'En cours', dotColor: 'bg-[#F97316]' },
        { status: 'En révision', dotColor: 'bg-[#7C3AED]' },
        { status: 'Terminé', dotColor: 'bg-emerald-500' }
      ]
    }
  },
  computed: {
    availableTypes() {
      const types = this.projects.map(p => p.project_type).filter(Boolean)
      return [...new Set(types)]
    }
  },
  methods: {
    async fetchProjects() {
      this.isLoading = true
      try {
        const res = await apiClient.get('/api/v1/projects')
        this.projects = res.data
      } catch (err) {
        console.error('Erreur chargement projets Kanban:', err)
      } finally {
        this.isLoading = false
      }
    },
    getProjectsByStatus(status) {
      return this.projects.filter(p => {
        const matchStatus = p.status === status
        const matchType = this.selectedType === 'Tous' || p.project_type === this.selectedType
        return matchStatus && matchType
      })
    },
    async moveProject(project, newStatus) {
      if (project.status === newStatus) return
      const previous = project.status
      project.status = newStatus

      try {
        await apiClient.patch(`/api/v1/projects/${project.id}`, {
          project: { status: newStatus }
        })
      } catch (err) {
        project.status = previous
        console.error('Erreur déplacement projet:', err)
      }
    }
  },
  mounted() {
    this.fetchProjects()
  }
}
</script>
